import { formatters } from "@tokenterminal/core/utils/sharedFormatter"
import { type BoxProps } from "@tokenterminal/ui/Box"
import { Divider } from "@tokenterminal/ui/Divider"
import { ErrorText } from "@tokenterminal/ui/ErrorText"
import { HeadingWithCapsize as Heading } from "@tokenterminal/ui/Heading"
import { ErrorBoundary } from "components/ErrorBoundary"
import { DataSnippet } from "components/v3/DataSnippet"
import { calculateTrend } from "components/v3/DataSnippet/DataSnippet.utils"
import { useMetricConfiguration } from "data/api/meta"
import { useNumberFormatter } from "hooks/useNumberFormatter"
import { Suspense, memo } from "react"
import { AsideCard } from "../Aside"

import {
  AsideList,
  AsideListItem,
  AsideListScrollContainer,
} from "../Aside/AsideList"
import { AsideSkeleton } from "../Aside/AsideListSkeletons"
import { TextFilter } from "../Aside/AsideTextFilter"
import { useFilteredData } from "../Aside/useFilteredData"
import {
  useMetricsList,
  useMetricParams,
  type MetricsListItem,
} from "./useMetricsList"

const filterBy: Array<keyof MetricsListItem> = ["name", "slug"]

const aggregationLabels = {
  median: "Median",
  sum: "Total",
} as const

const MetricsItem = memo(function MetricsItem({
  slug,
  name,
  metric_change,
  metric_value,
  metric_aggregation_type,
  chart_series: chartData,
  isActive,
  metric_interval,
}: MetricsListItem & { isActive: boolean }) {
  const metricConfig = useMetricConfiguration(slug)!
  const numberFormat = useNumberFormatter()

  let metricIntervalLabel =
    metric_interval === "latest" ? "" : `(${metric_interval})`

  // api returns "median" and "latest" but should be 24h
  if (metric_aggregation_type === "median" && metric_interval === "latest") {
    metricIntervalLabel = " (24h)"
  }

  const description: Array<string> = [
    aggregationLabels[metric_aggregation_type],
  ]
  if (metricIntervalLabel) {
    description.push(metricIntervalLabel)
  }
  description.push(" / ")
  description.push("24h%")

  return (
    <AsideListItem key={slug} href={`/metrics/${slug}`} id={slug} isShallow>
      <DataSnippet
        title={name}
        trend={calculateTrend({ metricConfig, metric_change })}
        description={description.join(" ")}
        value={
          typeof metric_value === "number"
            ? formatters[metricConfig.format as keyof typeof formatters]({
                value: metric_value,
                format: numberFormat,
              })
            : null
        }
        change={
          typeof metric_change === "number"
            ? formatters.percent({
                value: metric_change * 100,
              })
            : null
        }
        plotValues={chartData}
      />
    </AsideListItem>
  )
})

const MetricsList = () => {
  const { data } = useMetricsList()
  const { slug: metricSlug } = useMetricParams()
  const filteredData = useFilteredData(
    data.filter((metric) => metric.metric_id !== "net_deposits"),
    filterBy,
    "metric"
  )

  return (
    <AsideListScrollContainer>
      <AsideList currentPage={metricSlug}>
        {filteredData.map(({ slug, ...props }) => {
          const isCurrentPage = metricSlug === slug

          return (
            <MetricsItem
              key={slug}
              slug={slug}
              {...props}
              isActive={isCurrentPage}
            />
          )
        })}
      </AsideList>
    </AsideListScrollContainer>
  )
}

export const MetricsAside = (boxProps: BoxProps) => {
  const { slug } = useMetricParams()

  return (
    <AsideCard
      height="100%"
      isDefaultOpenInMobile={!slug}
      {...boxProps}
      title={
        <Heading level={4} as={slug ? "p" : "h1"} variant="secondary">
          Metrics
        </Heading>
      }
    >
      <TextFilter id="metric" autoFocus={!slug} />
      <Divider bleedX="s" />
      <ErrorBoundary fallback={<ErrorText>Failed to load metrics</ErrorText>}>
        <Suspense fallback={<AsideSkeleton />}>
          <MetricsList />
        </Suspense>
      </ErrorBoundary>
    </AsideCard>
  )
}
